import { Citation } from "@/store/modules/study/study.types";
import {
    EditCitationDTO,
    GetStudiesByScreeningTypeStatusPayload,
    GetStudiesByUserIdPayload,
    RevertStudyScreeningDecisionPayload,
    ScreeningRecordPayLoad,
    StudyScreeningRecord,
    UpdateStudyScreeningStatusPayload,
    RevertDecisionPayload,
    ResolveConflictPayload,
    StudyScreeningRecordsCountPayLoad,
    MergeStudiesPayload,
    ScreeningOverviewPayLoad
} from "@/store/modules/screening/screening.types";
import axios from "axios";

export const screeningApi = {
    /**
     * JIRA: SUMDEV-2964
     * New cluster-based screening counts with breakdown
     */
    getScreeningRecordsCountV2(projectId: number) {
        return axios.get(`projects/${projectId}/screening/records-count-v2`);
    },
    getScreeningOverviewCount(screeningOverviewPayload: ScreeningOverviewPayLoad) {
        const { projectId, userId } = screeningOverviewPayload;
        return axios.get(`projects/${projectId}/screening/${userId}/screening-overview-count`);
    },
    getStudyScreeningRecordsCount(studyScreeningRecordsCountPayload: StudyScreeningRecordsCountPayLoad) {
        const { projectId, userId, screeningType, search } = studyScreeningRecordsCountPayload;
        return axios.get(`projects/${projectId}/screening/${userId}/screening-records-count`, { params: { screeningType, search } });
    },
    getAwaitingStudyScreeningRecords(screeningRecordPayLoad: ScreeningRecordPayLoad) {
        const { projectId, userId, ...paginationPayload } = screeningRecordPayLoad;
        const awaitingScreenUrl = `projects/${projectId}/screening/${userId}/awaiting-screening-records`;
        return axios.get(awaitingScreenUrl, { params: paginationPayload });
    },
    editCitationRecord(citation: Citation) {
        const editCitationDTO: EditCitationDTO = citation;
        const projectId = citation.projectId;
        return axios.post(`projects/${projectId}/screening/edit-citation-record`, editCitationDTO);
    },
    getStudiesByScreeningTypeStatus({ projectId, screeningType, screeningStatus, ...paginationPayload }: GetStudiesByScreeningTypeStatusPayload) {
        return axios.get(`projects/${projectId}/screening/${screeningType}/studies/${screeningStatus}`, { params: paginationPayload });
    },
    getMyDecisionStudyScreeningRecords(screeningRecordPayLoad: GetStudiesByUserIdPayload) {
        const { projectId, userId, ...paginationPayload } = screeningRecordPayLoad;
        const myDecisionScreenUrl = `projects/${projectId}/screening/${userId}/my-decision-records`;
        return axios.get(myDecisionScreenUrl, { params: paginationPayload });
    },
    updateStudyScreeningStatus({ projectId, screeningType, studyId, ...payload }: UpdateStudyScreeningStatusPayload) {
        return axios.patch(`projects/${projectId}/screening/${screeningType}/studies/${studyId}`, payload, { timeout: 30000 });
    },
    revertStudyScreeningDecision({ projectId, screeningType, studyId }: RevertStudyScreeningDecisionPayload) {
        return axios.patch(`projects/${projectId}/screening/${screeningType}/studies/${studyId}/revert`);
    },
    revertMyDecisionStudyScreeningRecords(revertScreeningRecordPayLoad: Partial<StudyScreeningRecord>) {
        const { id, projectId, studyId, screeningStage } = revertScreeningRecordPayLoad;
        const revertMyDecisionUrl = `projects/${projectId}/screening/${screeningStage}/studies/${studyId}/revert-screening-record/${id}`;
        return axios.patch(revertMyDecisionUrl);
    },
    getConflictedStudyScreeningRecords(screeningRecordPayLoad: GetStudiesByUserIdPayload) {
        const { projectId, userId, ...paginationPayload } = screeningRecordPayLoad;
        const conflictedScreenUrl = `projects/${projectId}/screening/conflicted-screening-records`;
        return axios.get(conflictedScreenUrl, { params: paginationPayload });
    },
    getResolvedStudyScreeningRecords(screeningRecordPayLoad: GetStudiesByUserIdPayload) {
        const { projectId, userId, ...paginationPayload } = screeningRecordPayLoad;
        const resolvedScreeningRecords = `projects/${projectId}/screening/resolved-records`;
        return axios.get(resolvedScreeningRecords, { params: paginationPayload });
    },
    revertResolved(revertDecisionPayload: RevertDecisionPayload) {
        const { projectId, studyId, ...params } = revertDecisionPayload;
        const revertResolvedUrl = `projects/${projectId}/screening/${studyId}/revert-resolved`;
        return axios.post(revertResolvedUrl, revertDecisionPayload);
    },
    resolveConflict(resolveConflictPayload: ResolveConflictPayload) {
        const { projectId, studyId, ...params } = resolveConflictPayload;
        const resolveConflictUrl = `projects/${projectId}/screening/${studyId}/resolve-conflict`;
        return axios.post(resolveConflictUrl, resolveConflictPayload);
    },
    mergeStudies(payload: MergeStudiesPayload) {
        const { projectId, primaryStudyId, studyIds } = payload;
        const mergeStudiesUrl = `projects/${projectId}/screening/${primaryStudyId}/merge-studies`;
        return axios.post(mergeStudiesUrl, studyIds);
    },
    unmergedStudies(payload: MergeStudiesPayload) {
        const { projectId, primaryStudyId, studyIds } = payload;
        const unmergeStudiesUrl = `projects/${projectId}/screening/${primaryStudyId}/unmerged-studies`;
        return axios.post(unmergeStudiesUrl, studyIds);
    },
    getHighlightedKeywords(projectId: number) {
        const keywordUrl = `projects/${projectId}/screening/highlighted-keyword`;
        return axios.get(keywordUrl)
    }
};
